// SPDX-License-Identifier: AGPL-3.0-or-later

export const TrackingURLEncoding = Object.freeze({
	BASE64: 'base64',
	BASE32: 'base32',
	BASE45: 'base45',
	URL: 'url',
	URL_COMPONENT: 'urlComponent',
	BINARY: 'binary',
	HEX: 'hex',
} as const);

export type TrackingURLEncoding = (typeof TrackingURLEncoding)[keyof typeof TrackingURLEncoding];
export const TrackingURLHandler = Object.freeze({
	PATCHBOT: 'patchbot.io',
	PROOFPOINT_URL_DEFENSE: 'urldefense.proofpoint.com',
	STARDOCK_ENTERTAINMENT: 'stardockentertainment.info',
	STEAM: 'steam.gs',
	MJT: '0yxjo.mjt.lu',
	DOMINOS_NEW_ZEALAND: 'deals.dominos.co.nz',
	REDDIT_MAIL: 'click.redditmail.com',
	REDIRECTING_AT: 'redirectingat.com',
	TWITCH_EMAIL: 'twitch.tv-email',
} as const);

export type TrackingURLHandler = (typeof TrackingURLHandler)[keyof typeof TrackingURLHandler];

export interface TrackingURLAMPRule {
	readonly regex?: RegExp;
	readonly replace?: {
		readonly text: string | RegExp;
		readonly with?: string;
	};
	readonly sliceTrailing?: string;
}

interface TrackingURLDecodeRule {
	readonly param?: string;
	readonly jsonKey?: string;
	readonly encoding?: TrackingURLEncoding;
	readonly targetPath?: boolean;
	readonly handler?: TrackingURLHandler;
}

export interface TrackingURLRule {
	readonly name: string;
	readonly match: RegExp;
	readonly matchURL?: boolean;
	readonly hosts?: ReadonlyArray<string>;
	readonly removeParams?: ReadonlyArray<string>;
	readonly removeFromPath?: ReadonlyArray<RegExp>;
	readonly exclude?: ReadonlyArray<RegExp>;
	readonly redirectParam?: string;
	readonly AMP?: TrackingURLAMPRule;
	readonly decode?: TrackingURLDecodeRule;
	readonly allowParams?: ReadonlyArray<string>;
	readonly removeEmptyParamValues?: boolean;
}

function defineTrackingURLRules(rules: ReadonlyArray<TrackingURLRule>): ReadonlyArray<TrackingURLRule> {
	for (const rule of rules) {
		const rewritesURL = rule.redirectParam != null || rule.decode != null || rule.AMP != null;
		if (rewritesURL && rule.matchURL !== true && rule.hosts == null) {
			throw new Error(`Tracking URL rewrite rule ${rule.name} must declare hosts`);
		}
		freezeTrackingURLRule(rule);
	}
	return Object.freeze(rules);
}

function freezeTrackingURLRule(rule: TrackingURLRule): void {
	const listValues: ReadonlyArray<ReadonlyArray<unknown> | undefined> = [
		rule.hosts,
		rule.removeParams,
		rule.removeFromPath,
		rule.exclude,
		rule.allowParams,
	];
	for (const listValue of listValues) {
		if (listValue != null) {
			Object.freeze(listValue);
		}
	}
	const ampRule = rule.AMP;
	if (ampRule != null) {
		if (ampRule.replace != null) {
			Object.freeze(ampRule.replace);
		}
		Object.freeze(ampRule);
	}
	if (rule.decode != null) {
		Object.freeze(rule.decode);
	}
	Object.freeze(rule);
}

export const TRACKING_URL_RULES: ReadonlyArray<TrackingURLRule> = defineTrackingURLRules([
	{
		name: 'Global',
		match: /.*/,
		removeParams: [
			'utm_source',
			'utm_medium',
			'utm_term',
			'utm_campaign',
			'utm_content',
			'utm_name',
			'utm_cid',
			'utm_reader',
			'utm_viz_id',
			'utm_pubreferrer',
			'utm_swu',
			'utm_social-type',
			'utm_brand',
			'utm_team',
			'utm_feeditemid',
			'utm_id',
			'utm_marketing_tactic',
			'utm_creative_format',
			'utm_campaign_id',
			'utm_source_platform',
			'utm_timestamp',
			'utm_souce',
			'utm_couponvalue',
			'itm_source',
			'itm_medium',
			'itm_term',
			'itm_campaign',
			'itm_content',
			'itm_channel',
			'itm_source_s',
			'itm_medium_s',
			'itm_campaign_s',
			'itm_audience',
			'int_source',
			'int_cmp_name',
			'int_cmp_id',
			'int_cmp_creative',
			'int_medium',
			'int_campaign',
			'int_content',
			'pk_campaign',
			'pk_cpn',
			'pk_source',
			'pk_medium',
			'pk_keyword',
			'pk_kwd',
			'pk_content',
			'pk_cid',
			'piwik_campaign',
			'piwik_cpn',
			'piwik_source',
			'piwik_medium',
			'piwik_keyword',
			'piwik_kwd',
			'piwik_content',
			'piwik_cid',
			'gclid',
			'ga_source',
			'ga_medium',
			'ga_term',
			'ga_content',
			'ga_campaign',
			'ga_place',
			'gclsrc',
			'hsa_cam',
			'hsa_grp',
			'hsa_mt',
			'hsa_src',
			'hsa_ad',
			'hsa_acc',
			'hsa_net',
			'hsa_kw',
			'hsa_tgt',
			'hsa_ver',
			'hsa_la',
			'hsa_ol',
			'fbclid',
			'oly_enc_id',
			'oly_anon_id',
			'vero_id',
			'vero_conv',
			'__s',
			'_hsenc',
			'_hsmi',
			'__hssc',
			'__hstc',
			'__hsfp',
			'hsCtaTracking',
			'mkt_tok',
			'mtm_campaign',
			'mtm_keyword',
			'mtm_kwd',
			'mtm_source',
			'mtm_medium',
			'mtm_content',
			'mtm_cid',
			'mtm_group',
			'mtm_placement',
			'elqTrackId',
			'elq',
			'elqak',
			'elqaid',
			'elqat',
			'elqCampaignId',
			'elqTrack',
			'mc_cid',
			'mc_eid',
			'ncid',
			'cmpid',
			'mbid',
			'rdt_cid',
		],
	},
	{
		name: 'audible.com',
		match: /www.audible.com/i,
		removeParams: ['qid', 'sr', 'pf_rd_p', 'pf_rd_r', 'plink', 'ref'],
	},
	{
		name: 'bandcamp.com',
		match: /.*.bandcamp.com/gi,
		removeParams: [
			'from',
			'search_item_id',
			'search_item_type',
			'search_match_part',
			'search_page_id',
			'search_page_no',
			'search_rank',
			'search_sig',
		],
	},
	{
		name: 'amazon.com',
		match: /amazon\.[a-z0-9]{0,3}/i,
		removeParams: [
			'psc',
			'colid',
			'coliid',
			'linkId',
			'tag',
			'linkCode',
			'ms3_c',
			'pf_rd_s',
			'pf_rd_t',
			'pf_rd_i',
			'pf_rd_m',
			'pd_rd_w',
			'qid',
			'sr',
			'keywords',
			'dchild',
			'ref',
			'ref_',
			'rnid',
			'pf_rd_r',
			'pf_rd_p',
			'pd_rd_r',
			'smid',
			'pd_rd_wg',
			'content-id',
			'spLa',
			'crid',
			'sprefix',
			'hvlocint',
			'hvdvcmdl',
			'hvptwo',
			'hvpone',
			'hvpos',
			'qu',
			'pd_rd_i',
			'nc2',
			'nc1',
			'trk',
			'sc_icampaign',
			'trkCampaign',
			'ufe',
			'sc_icontent',
			'sc_ichannel',
			'sc_iplace',
			'sc_country',
			'sc_outcome',
			'sc_geo',
			'sc_campaign',
			'sc_channel',
		],
		removeFromPath: [/(\/ref|&ref_)=[^/?]*/i],
	},
	{
		name: 'reddit.com',
		match: /.*.reddit.com/i,
		removeParams: [
			'ref_campaign',
			'ref_source',
			'tags',
			'keyword',
			'channel',
			'campaign',
			'user_agent',
			'domain',
			'base_url',
			'$android_deeplink_path',
			'$deeplink_path',
			'$og_redirect',
			'share_id',
			'correlation_id',
			'ref',
			'rdt',
		],
	},
	{
		name: 'app.link',
		match: /.*\.app\.link/i,
		removeParams: [
			'tags',
			'keyword',
			'channel',
			'campaign',
			'user_agent',
			'domain',
			'base_url',
			'$android_deeplink_path',
			'$deeplink_path',
			'$og_redirect',
			'compact_view',
			'dnt',
			'adblock',
			'geoip_country',
			'referrer_domain',
			'referrer_url',
		],
	},
	{
		name: 'twitch.tv',
		match: /www.twitch.tv/i,
		removeParams: ['tt_medium', 'tt_content', 'tt_email_id'],
	},
	{
		name: 'twitch.tv-email',
		matchURL: true,
		match: /www.twitch.tv\/r\/e/i,
		decode: {handler: 'twitch.tv-email', targetPath: true},
	},
	{
		name: 'blog.twitch.tv',
		match: /blog.twitch.tv/i,
		removeParams: ['utm_referrer'],
	},
	{
		name: 'pixiv.net',
		match: /www.pixiv.net/i,
		removeParams: ['p', 'i', 'g'],
	},
	{
		name: 'spotify.com',
		match: /open.spotify.com/i,
		removeParams: ['si', 'utm_source', 'context', 'sp_cid', '_branch_match_id', '_branch_referrer'],
		allowParams: ['go', 'nd'],
	},
	{
		name: 'aliexpress.com',
		match: /^(?:https?:\/\/)?(?:[^.]+\.)?aliexpress\.(?:[a-z]{2,}){1,}/i,
		removeParams: [
			'_t',
			'spm',
			'algo_pvid',
			'algo_expid',
			'btsid',
			'ws_ab_test',
			'initiative_id',
			'origin',
			'widgetId',
			'tabType',
			'productId',
			'productIds',
			'gps-id',
			'scm',
			'scm_id',
			'scm-url',
			'pvid',
			'algo_exp_id',
			'pdp_pi',
			'fromRankId',
			'sourceType',
			'utparam',
			'gatewayAdapt',
			'_evo_buckets',
			'tpp_rcmd_bucket_id',
			'scenario',
			'pdp_npi',
			'tt',
			'spreadType',
			'srcSns',
			'bizType',
			'social_params',
			'aff_fcid',
			'aff_fsk',
			'aff_platform',
			'aff_trace_key',
			'shareId',
			'platform',
			'businessType',
			'terminal_id',
			'afSmartRedirect',
			'sk',
			'gbraid',
		],
		allowParams: ['sku_id', 'pdp_ext_f'],
	},
	{
		name: 'google.com',
		hosts: ['google.com'],
		match: /www.google\..*/i,
		removeParams: [
			'sourceid',
			'client',
			'aqs',
			'sxsrf',
			'uact',
			'ved',
			'iflsig',
			'source',
			'ei',
			'oq',
			'gs_lcp',
			'sclient',
			'bih',
			'biw',
			'sa',
			'dpr',
			'rlz',
			'gs_lp',
			'sca_esv',
			'si',
			'gs_l',
			'gs_lcrp',
		],
		AMP: {
			regex: /www\.google\.(?:.*)\/amp\/s\/(.*)/gim,
		},
		redirectParam: 'url',
	},
	{
		name: 'youtube.com',
		hosts: ['youtube.com'],
		match: /.*.youtube.com/i,
		removeParams: ['gclid', 'feature', 'app', 'src', 'lId', 'cId', 'embeds_referring_euri'],
		redirectParam: 'q',
	},
	{
		name: 'humblebundle.com',
		match: /www.humblebundle.com/i,
		removeParams: ['hmb_source', 'hmb_medium', 'hmb_campaign', 'mcID', 'linkID'],
		allowParams: ['partner'],
	},
	{
		name: 'greenmangaming.com',
		match: /www.greenmangaming.com/i,
		removeParams: ['CJEVENT', 'cjevent', 'irclickid', 'irgwc', 'pdpgatetoken'],
	},
	{
		name: 'fanatical.com',
		match: /www.fanatical.com/i,
		removeParams: ['cj_pid', 'cj_aid', 'aff_track', 'CJEVENT', 'cjevent'],
	},
	{
		name: 'newsweek.com',
		match: /www.newsweek.com/i,
		removeParams: ['subref', 'amp'],
	},
	{
		name: 'imgur.com',
		match: /imgur.com/i,
		removeParams: ['source'],
	},
	{
		name: 'plex.tv',
		match: /.*.plex.tv/i,
		removeParams: ['origin', 'plex_utm', 'sl', 'ckhid'],
	},
	{
		name: 'imdb.com',
		match: /^.*\.imdb\.com/i,
		removeParams: [
			'ref_',
			'ref\\_',
			'pf_rd_m',
			'pf_rd_r',
			'pf_rd_p',
			'pf_rd_s',
			'pf_rd_t',
			'pf_rd_i',
			'ref_hp_hp_e_2',
			'rf',
			'ref',
		],
	},
	{
		name: 'gog.com',
		match: /www.gog.com/i,
		removeParams: ['at_gd', 'rec_scenario_id', 'rec_sub_source_id', 'rec_item_id', 'vds_id', 'prod_id', 'rec_source'],
	},
	{
		name: 'tiktok.com',
		match: /www.tiktok.com/i,
		removeParams: [
			'is_copy_url',
			'is_from_webapp',
			'sender_device',
			'sender_web_id',
			'sec_user_id',
			'share_app_id',
			'share_item_id',
			'share_link_id',
			'social_sharing',
			'_r',
			'source',
			'user_id',
			'u_code',
			'tt_from',
			'share_author_id',
			'sec_uid',
			'checksum',
			'_d',
			'refer',
			'enter_from',
			'enter_method',
			'attr_medium',
			'attr_source',
		],
		allowParams: ['lang'],
	},
	{
		name: 'tiktok.com/link',
		match: /tiktok\.com\/link\/v2/i,
		matchURL: true,
		redirectParam: 'target',
	},
	{
		name: 'facebook.com',
		hosts: ['facebook.com'],
		match: /.*.facebook.com/i,
		removeParams: ['fbclid', 'fb_ref', 'fb_source', 'referral_code', 'referral_story_type', 'tracking', 'ref'],
		redirectParam: 'u',
		exclude: [/www\.facebook\.com\/sharer/gi],
	},
	{
		name: 'yandex.com',
		match: /yandex.com/i,
		removeParams: ['lr', 'from', 'grhow', 'origin', '_openstat'],
	},
	{
		name: 'store.steampowered.com',
		match: /store.steampowered.com/i,
		removeParams: ['snr'],
	},
	{
		name: 'findojobs.co.nz',
		match: /www.findojobs.co.nz/i,
		removeParams: ['source'],
	},
	{
		name: 'linkedin.com',
		match: /.*.linkedin.com/i,
		removeParams: [
			'contextUrn',
			'destRedirectURL',
			'lipi',
			'licu',
			'trk',
			'trkInfo',
			'originalReferer',
			'upsellOrderOrigin',
			'upsellTrk',
			'upsellTrackingId',
			'src',
			'trackingId',
			'midToken',
			'midSig',
			'trkEmail',
			'eid',
		],
		allowParams: ['otpToken'],
	},
	{
		name: 'indeed.com',
		match: /.*.indeed.com/i,
		removeParams: ['from', 'attributionid'],
	},
	{
		name: 'discord.com',
		match: /(?:.*\.)?discord\.com/i,
		removeParams: ['source', 'ref'],
	},
	{
		name: 'medium.com',
		match: /medium.com/i,
		removeParams: ['source'],
	},
	{
		name: 'twitter.com',
		match: /twitter.com/i,
		removeParams: ['s', 'src', 'ref_url', 'ref_src'],
	},
	{
		name: 'voidu.com',
		match: /voidu.com/i,
		removeParams: ['affiliate'],
	},
	{
		name: 'wingamestore.com',
		match: /wingamestore.com/i,
		removeParams: ['ars'],
	},
	{
		name: 'gamebillet.com',
		match: /gamebillet.com/i,
		removeParams: ['affiliate'],
	},
	{
		name: 'gamesload.com',
		match: /^www.gamesload.com/i,
		removeParams: ['affil'],
		allowParams: ['REF'],
	},
	{
		name: 'mightyape',
		match: /mightyape.(co.nz|com.au)/i,
		removeParams: ['m'],
	},
	{
		name: 'apple.com',
		match: /.*.apple.com/i,
		removeParams: ['uo', 'app', 'at', 'ct', 'ls', 'pt', 'mt', 'itsct', 'itscg', 'referrer', 'src', 'cid'],
	},
	{
		name: 'music.apple.com',
		match: /music.apple.com/i,
		removeParams: ['i', 'lId', 'cId', 'sr', 'src'],
	},
	{
		name: 'play.google.com',
		match: /play.google.com/i,
		removeParams: ['referrer', 'pcampaignid'],
	},
	{
		name: 'adtraction.com',
		hosts: ['adtraction.com'],
		match: /adtraction.com/i,
		redirectParam: 'url',
	},
	{
		name: 'dpbolvw.net',
		hosts: ['dpbolvw.net'],
		match: /dpbolvw.net/i,
		redirectParam: 'url',
	},
	{
		name: 'lenovo.com',
		match: /.*.lenovo.com/i,
		removeParams: ['PID', 'clickid', 'irgwc', 'cid', 'acid', 'linkTrack'],
	},
	{
		name: 'itch.io',
		match: /itch.io/i,
		removeParams: ['fbclid'],
	},
	{
		name: 'steamcommunity.com',
		hosts: ['steamcommunity.com'],
		match: /steamcommunity.com/i,
		redirectParam: 'url',
	},
	{
		name: 'steamcommunity.com/linkfilter',
		match: /steamcommunity.com\/linkfilter/i,
		redirectParam: 'u',
		matchURL: true,
	},
	{
		name: 'microsoft.com',
		match: /microsoft.com/i,
		removeParams: ['refd', 'icid'],
	},
	{
		name: 'berrybase.de',
		match: /berrybase.de/i,
		removeParams: ['sPartner'],
	},
	{
		name: 'instagram.com',
		hosts: ['instagram.com'],
		match: /instagram.com/i,
		removeParams: ['igshid', 'igsh', 'source'],
		redirectParam: 'u',
	},
	{
		name: 'hubspot.com',
		match: /hubspot.com/i,
		removeParams: ['hubs_content-cta', 'hubs_content'],
	},
	{
		name: 'ebay.com',
		match: /^(?:https?:\/\/)?(?:[^.]+\.)?ebay\.[a-z0-9]{0,3}/i,
		removeParams: [
			'amdata',
			'var',
			'hash',
			'_trkparms',
			'_trksid',
			'_from',
			'mkcid',
			'mkrid',
			'campid',
			'toolid',
			'mkevt',
			'customid',
			'siteid',
			'ufes_redirect',
			'ff3',
			'pub',
			'media',
			'widget_ver',
			'ssspo',
			'sssrc',
			'ssuid',
		],
		allowParams: ['epid', '_nkw'],
	},
	{
		name: 'shopee.com',
		match: /^(?:https?:\/\/)?(?:[^.]+\.)?shopee\.[a-z0-9]{0,3}/i,
		removeParams: [
			'af_siteid',
			'pid',
			'af_click_lookback',
			'af_viewthrough_lookback',
			'is_retargeting',
			'af_reengagement_window',
			'af_sub_siteid',
			'c',
		],
	},
	{
		name: 'msn.com',
		match: /www.msn.com/i,
		removeParams: ['ocid', 'cvid', 'pc'],
	},
	{
		name: 'nuuvem.com',
		match: /www.nuuvem.com/i,
		removeParams: ['ranMID', 'ranEAID', 'ranSiteID'],
	},
	{
		name: 'sjv.io',
		hosts: ['sjv.io'],
		match: /.*.sjv.io/i,
		redirectParam: 'u',
	},
	{
		name: 'linksynergy.com',
		hosts: ['linksynergy.com'],
		match: /.*.linksynergy.com/i,
		removeParams: ['id', 'mid'],
		redirectParam: 'murl',
	},
	{
		name: 'cnbc.com',
		match: /www.cnbc.com/i,
		removeParams: ['__source'],
	},
	{
		name: 'yahoo.com',
		match: /yahoo.com/i,
		removeParams: ['guce_referrer', 'guce_referrer_sig', 'guccounter', 'soc_src', 'soc_trk', 'tsrc'],
	},
	{
		name: 'techcrunch.com',
		match: /techcrunch.com/i,
		removeParams: ['guce_referrer', 'guce_referrer_sig', 'guccounter'],
	},
	{
		name: 'office.com',
		match: /office.com/i,
		removeParams: ['from'],
	},
	{
		name: 'ticketmaster.co.nz',
		match: /ticketmaster.co.nz/i,
		removeParams: ['tm_link'],
	},
	{
		name: 'bostonglobe.com',
		match: /bostonglobe.com/i,
		removeParams: ['p1'],
	},
	{
		name: 'ampproject.org',
		hosts: ['ampproject.org'],
		match: /cdn.ampproject.org/i,
		removeParams: ['amp_gsa', 'amp_js_v', 'usqp', 'outputType'],
		AMP: {
			regex: /cdn\.ampproject\.org\/v\/s\/(.*)#(aoh|csi|referrer|amp)/gim,
		},
	},
	{
		name: 'nbcnews.com',
		hosts: ['nbcnews.com'],
		match: /nbcnews.com/i,
		removeParams: ['fbclid'],
		AMP: {
			replace: {
				text: 'www.nbcnews.com/news/amp/',
				with: 'www.nbcnews.com/news/',
			},
		},
	},
	{
		name: 'countdown.co.nz',
		match: /www.countdown.co.nz/i,
		removeParams: ['promo_name', 'promo_creative', 'promo_position', 'itemID'],
	},
	{
		name: 'etsy.com',
		match: /www.etsy.com/i,
		removeParams: ['click_key', 'click_sum', 'rec_type', 'ref', 'frs', 'sts', 'dd_referrer'],
	},
	{
		name: 'wattpad.com',
		match: /www.wattpad.com/i,
		removeParams: ['wp_page', 'wp_uname', 'wp_originator'],
	},
	{
		name: 'redirect.viglink.com',
		hosts: ['redirect.viglink.com'],
		match: /redirect.viglink.com/i,
		redirectParam: 'u',
	},
	{
		name: 'noctre.com',
		match: /www.noctre.com/i,
		removeParams: ['aff'],
	},
	{
		name: 'dreamgame.com',
		match: /www.dreamgame.com/i,
		removeParams: ['affiliate'],
	},
	{
		name: 'startpage.com',
		match: /.*.startpage.com/i,
		removeParams: ['source'],
	},
	{
		name: '2game.com',
		match: /^2game.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'jdoqocy.com',
		hosts: ['jdoqocy.com'],
		match: /^www.jdoqocy.com/i,
		redirectParam: 'url',
	},
	{
		name: 'gamesplanet.com',
		match: /^(?:.*\.|)gamesplanet\.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'gamersgate.com',
		match: /www.gamersgate.com/i,
		removeParams: ['aff'],
	},
	{
		name: 'gate.sc',
		hosts: ['gate.sc'],
		match: /gate.sc/i,
		redirectParam: 'url',
	},
	{
		name: 'getmusicbee.com',
		hosts: ['getmusicbee.com'],
		match: /^getmusicbee.com/i,
		redirectParam: 'r',
	},
	{
		name: 'imp.i305175.net',
		hosts: ['imp.i305175.net'],
		match: /^imp.i305175.net/i,
		redirectParam: 'u',
	},
	{
		name: 'qflm.net',
		hosts: ['qflm.net'],
		match: /.*.qflm.net/i,
		redirectParam: 'u',
	},
	{
		name: 'anrdoezrs.net',
		hosts: ['anrdoezrs.net'],
		match: /anrdoezrs.net/i,
		AMP: {
			regex: /(?:.*)\/links\/(?:.*)\/type\/dlg\/sid\/\[subid_value\]\/(.*)/gi,
		},
	},
	{
		name: 'emjcd.com',
		hosts: ['emjcd.com'],
		match: /^www.emjcd.com/i,
		decode: {
			param: 'd',
			jsonKey: 'destinationUrl',
		},
	},
	{
		name: 'go2cloud.org',
		hosts: ['go2cloud.org'],
		match: /^.*.go2cloud.org/i,
		redirectParam: 'aff_unique1',
	},
	{
		name: 'bn5x.net',
		hosts: ['bn5x.net'],
		match: /^.*.bn5x.net/i,
		redirectParam: 'u',
	},
	{
		name: 'tvguide.com',
		hosts: ['tvguide.com'],
		match: /^www.tvguide.com/i,
		AMP: {regex: /(.*)#link=/i},
	},
	{
		name: 'ranker.com',
		match: /^(www|blog).ranker.com/i,
		removeParams: ['ref', 'rlf', 'l', 'li_source', 'li_medium'],
	},
	{
		name: 'tkqlhce.com',
		hosts: ['tkqlhce.com'],
		match: /^www.tkqlhce.com/i,
		redirectParam: 'url',
	},
	{
		name: 'flexlinkspro.com',
		hosts: ['flexlinkspro.com'],
		match: /^track.flexlinkspro.com/i,
		redirectParam: 'url',
	},
	{
		name: 'watchworthy.app',
		match: /^watchworthy.app/i,
		removeParams: ['ref'],
	},
	{
		name: 'hbomax.com',
		hosts: ['hbomax.com'],
		match: /^trk.hbomax.com/i,
		redirectParam: 'url',
	},
	{
		name: 'squarespace.com',
		match: /^.*.squarespace.com/i,
		removeParams: ['subchannel', 'source', 'subcampaign', 'campaign', 'channel', '_ga'],
	},
	{
		name: 'baidu.com',
		match: /^www.baidu.com/i,
		removeParams: [
			'rsv_spt',
			'rsv_idx',
			'rsv_pq',
			'rsv_t',
			'rsv_bp',
			'rsv_dl',
			'rsv_iqid',
			'rsv_enter',
			'rsv_sug1',
			'rsv_sug2',
			'rsv_sug3',
			'rsv_sug4',
			'rsv_sug5',
			'rsv_sug6',
			'rsv_sug7',
			'rsv_sug8',
			'rsv_sug9',
			'rsv_btype',
			'tn',
			'sa',
			'rsf',
			'rqid',
			'usm',
			'__pc2ps_ab',
			'p_signature',
			'p_sign',
			'p_timestamp',
			'p_tk',
			'oq',
		],
		allowParams: ['wd', 'ie'],
	},
	{
		name: 'primevideo.com',
		match: /^www.primevideo.com/i,
		removeParams: ['dclid'],
		removeFromPath: [/\/ref=[^/?]*/i],
	},
	{
		name: 'threadless.com',
		match: /^www.threadless.com/i,
		removeParams: ['itm_source_s', 'itm_medium_s', 'itm_campaign_s'],
	},
	{
		name: 'wsj.com',
		match: /^www.wsj.com/i,
		removeParams: ['mod'],
	},
	{
		name: 'thewarehouse.co.nz',
		match: /^www.thewarehouse.co.nz/i,
		removeParams: ['sfmc_j', 'sfmc_id', 'sfmc_mid', 'sfmc_uid', 'sfmc_activityid'],
	},
	{
		name: 'awstrack.me',
		hosts: ['awstrack.me'],
		match: /^.*awstrack.me/i,
		AMP: {regex: /awstrack.me\/L0\/(.*)/},
	},
	{
		name: 'express.co.uk',
		match: /^www.express.co.uk/i,
		removeFromPath: [/\/amp$/i],
	},
	{
		name: 'ko-fi.com',
		match: /^ko-fi.com/i,
		removeParams: ['ref', 'src'],
	},
	{
		name: 'indiegala.com',
		match: /^www.indiegala.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'l.messenger.com',
		hosts: ['l.messenger.com'],
		match: /^l.messenger.com/i,
		redirectParam: 'u',
	},
	{
		name: 'transparency.fb.com',
		match: /^transparency.fb.com/i,
		removeParams: ['source'],
	},
	{
		name: 'manymorestores.com',
		match: /^www.manymorestores.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'macgamestore.com',
		match: /^www.macgamestore.com/i,
		removeParams: ['ars'],
	},
	{
		name: 'blizzardgearstore.com',
		match: /^www.blizzardgearstore.com/i,
		removeParams: ['_s'],
	},
	{
		name: 'playbook.com',
		match: /^www.playbook.com/i,
		removeParams: ['p'],
	},
	{
		name: 'cookiepro.com',
		match: /^.*.cookiepro.com/i,
		removeParams: ['source', 'referral'],
	},
	{
		name: 'pinterest.com',
		match: /^www\.pinterest\..*/i,
		removeParams: ['rs'],
	},
	{
		name: 'bing.com',
		match: /^www\.bing\.com/i,
		removeParams: [
			'qs',
			'form',
			'sp',
			'pq',
			'sc',
			'sk',
			'cvid',
			'FORM',
			'ck',
			'simid',
			'thid',
			'cdnurl',
			'pivotparams',
			'ghsh',
			'ghacc',
			'ccid',
			'',
			'ru',
		],
		allowParams: ['q', 'tsc', 'iss', 'id', 'view', 'setlang'],
	},
	{
		name: 'jf79.net',
		match: /^jf79\.net/i,
		removeParams: ['li', 'wi', 'ws', 'ws2'],
	},
	{
		name: 'frankenergie.nl',
		match: /^www\.frankenergie\.nl/i,
		removeParams: ['aff_id'],
	},
	{
		name: 'nova.cz',
		match: /^.*\.nova\.cz/i,
		removeParams: ['sznclid'],
	},
	{
		name: 'cnn.com',
		hosts: ['cnn.com'],
		match: /.*.cnn.com/i,
		removeParams: ['hpt', 'iid'],
		AMP: {
			replace: {
				text: 'amp.cnn.com/cnn/',
				with: 'www.cnn.com/',
			},
		},
		exclude: [/e.newsletters.cnn.com/gi],
	},
	{
		name: 'amp.scmp.com',
		hosts: ['amp.scmp.com'],
		match: /amp\.scmp\.com/i,
		AMP: {
			replace: {
				text: 'amp.scmp.com',
				with: 'scmp.com',
			},
		},
	},
	{
		name: 'justwatch.com',
		hosts: ['justwatch.com'],
		match: /click\.justwatch\.com/i,
		removeParams: ['cx', 'uct_country', 'uct_buybox', 'sid'],
		redirectParam: 'r',
	},
	{
		name: 'psychologytoday.com',
		match: /www\.psychologytoday\.com/i,
		removeParams: ['amp'],
	},
	{
		name: 'mouser.com',
		match: /www\.mouser\.com/i,
		removeParams: ['qs'],
	},
	{
		name: 'awin1.com',
		hosts: ['awin1.com'],
		match: /www\.awin1\.com/i,
		redirectParam: 'ued',
		removeParams: ['awinmid', 'awinaffid', 'clickref'],
	},
	{
		name: 'syteapi.com',
		hosts: ['syteapi.com'],
		match: /syteapi\.com/i,
		decode: {param: 'url', encoding: 'base64'},
	},
	{
		name: 'castorama.fr',
		match: /www\.castorama\.fr/i,
		removeParams: ['syte_ref'],
	},
	{
		name: 'quizlet.com',
		match: /quizlet\.com/i,
		removeParams: ['funnelUUID', 'source'],
	},
	{
		name: 'pbtech.co.nz',
		match: /www\.pbtech\.co\.nz/i,
		removeParams: ['qr'],
	},
	{
		name: 'matomo.org',
		match: /matomo\.org/i,
		removeParams: [
			'menu',
			'footer',
			'header',
			'hp-reasons-learn',
			'hp-top',
			'above-fold',
			'step1-hp',
			'mid-hp',
			'take-back-control-hp',
			'hp-reasons-icon',
			'hp-reasons-heading',
			'hp-reasons-p',
			'hp-bottom',
		],
	},
	{
		name: 'eufy.com',
		match: /eufy\.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'newsflare.com',
		match: /www\.newsflare\.com/i,
		removeParams: ['jwsource'],
	},
	{
		name: 'wish.com',
		match: /www\.wish\.com/i,
		removeParams: ['share'],
	},
	{
		name: 'change.org',
		match: /www\.change\.org/i,
		removeParams: ['source_location'],
	},
	{
		name: 'washingtonpost.com',
		match: /.*\.washingtonpost\.com/i,
		removeParams: ['itid', 's_l'],
	},
	{
		name: 'lowes.com',
		match: /www\.lowes\.com/i,
		removeParams: ['cm_mmc', 'ds_rl', 'gbraid'],
	},
	{
		name: 'stacks.wellcomecollection.org',
		match: /stacks\.wellcomecollection\.org/i,
		removeParams: ['source'],
	},
	{
		name: 'redbubble.com',
		match: /.*\.redbubble\.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'inyourarea.co.uk',
		match: /inyourarea.co.uk/i,
		removeParams: ['from_reach_primary_nav', 'from_reach_footer_nav', 'branding'],
	},
	{
		name: 'fiverr.com',
		match: /.*\.fiverr\.com/i,
		removeParams: ['source', 'context_referrer', 'referrer_gig_slug', 'ref_ctx_id', 'funnel', 'imp_id'],
	},
	{
		name: 'kqzyfj.com',
		hosts: ['kqzyfj.com'],
		match: /www\.kqzyfj\.com/i,
		redirectParam: 'url',
		removeParams: ['cjsku', 'pubdata'],
	},
	{
		name: 'marca.com',
		match: /.*\.marca\.com/i,
		removeParams: ['intcmp', 's_kw', 'emk'],
	},
	{
		name: 'marcaentradas.com',
		match: /.*\.marcaentradas\.com/i,
		removeParams: ['intcmp', 's_kw'],
	},
	{
		name: 'honeycode.aws',
		match: /.*\.honeycode\.aws/i,
		removeParams: [
			'trackingId',
			'sc_icampaign',
			'sc_icontent',
			'sc_ichannel',
			'sc_iplace',
			'sc_country',
			'sc_outcome',
			'sc_geo',
			'sc_campaign',
			'sc_channel',
			'trkCampaign',
			'trk',
		],
	},
	{
		name: 'news.artnet.com',
		match: /news\.artnet\.com/i,
		removeFromPath: [/\/amp-page$/i],
	},
	{
		name: 'studentbeans.com',
		match: /www\.studentbeans\.com/i,
		removeParams: ['source'],
	},
	{
		name: 'boxofficemojo.com',
		match: /boxofficemojo.com/i,
		removeParams: ['ref_'],
	},
	{
		name: 'solodeportes.com.ar',
		match: /www.solodeportes.com.ar/i,
		removeParams: ['nosto', 'refSrc'],
	},
	{
		name: 'amp.dw.com',
		hosts: ['amp.dw.com'],
		match: /amp.dw.com/i,
		AMP: {
			replace: {
				text: 'amp.dw.com',
				with: 'dw.com',
			},
		},
	},
	{
		name: 'joybuggy.com',
		match: /joybuggy.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'etail.market',
		match: /etail.market/i,
		removeParams: ['tracking'],
	},
	{
		name: 'myanimelist.net',
		match: /myanimelist.net/i,
		removeParams: ['_location', 'click_type', 'click_param'],
	},
	{
		name: 'support-dev.discord.com',
		match: /support-dev.discord.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'dlgamer.com',
		match: /dlgamer.com/i,
		removeParams: ['affil'],
	},
	{
		name: 'newsletter.manor.ch',
		match: /newsletter.manor.ch/i,
		removeParams: ['user_id_1'],
		removeEmptyParamValues: true,
	},
	{
		name: 'knowyourmeme.com',
		hosts: ['knowyourmeme.com'],
		match: /amp.knowyourmeme.com/i,
		AMP: {
			replace: {
				text: 'amp.knowyourmeme.com',
				with: 'knowyourmeme.com',
			},
		},
	},
	{
		name: 'ojrq.net',
		hosts: ['ojrq.net'],
		match: /ojrq.net/i,
		redirectParam: 'return',
	},
	{
		name: 'click.pstmrk.it',
		hosts: ['click.pstmrk.it'],
		match: /click.pstmrk.it/i,
		AMP: {regex: /click\.pstmrk\.it\/(?:[a-zA-Z0-9]){1,2}\/(.*?)\//gim},
	},
	{
		name: 'track.roeye.co.nz',
		hosts: ['track.roeye.co.nz'],
		match: /track.roeye.co.nz/i,
		redirectParam: 'path',
	},
	{
		name: 'producthunt.com',
		match: /producthunt.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'cbsnews.com',
		hosts: ['cbsnews.com'],
		match: /www.cbsnews.com/i,
		removeParams: ['ftag', 'intcid'],
		AMP: {
			replace: {
				text: 'cbsnews.com/amp/',
				with: 'cbsnews.com/',
			},
		},
	},
	{
		name: 'jobs.venturebeat.com',
		match: /jobs.venturebeat.com/i,
		removeParams: ['source'],
	},
	{
		name: 'api.ffm.to',
		hosts: ['api.ffm.to'],
		match: /api.ffm.to/i,
		decode: {
			param: 'cd',
			jsonKey: 'destUrl',
		},
	},
	{
		name: 'wfaa.com',
		match: /www.wfaa.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'buyatoyota.com',
		match: /www.buyatoyota.com/i,
		removeParams: ['siteid'],
	},
	{
		name: 'independent.co.uk',
		match: /www.independent.co.uk/i,
		removeParams: ['amp', 'regSourceMethod'],
	},
	{
		name: 'lenovo.vzew.net',
		hosts: ['lenovo.vzew.net'],
		match: /lenovo.vzew.net/i,
		redirectParam: 'u',
	},
	{
		name: 'stats.newswire.com',
		hosts: ['stats.newswire.com'],
		match: /stats.newswire.com/i,
		decode: {param: 'final'},
	},
	{
		name: 'cooking.nytimes.com',
		match: /cooking.nytimes.com/i,
		removeParams: ['smid', 'variant', 'algo', 'req_id', 'surface', 'imp_id', 'action', 'region', 'module', 'pgType'],
	},
	{
		name: 'optigruen.com',
		match: /www\.optigruen\.[a-z0-9]{0,3}/i,
		removeParams: ['cHash', 'chash', 'mdrv'],
	},
	{
		name: 'osi.rosenberger.com',
		match: /osi.rosenberger.com/i,
		removeParams: ['cHash', 'chash'],
	},
	{
		name: 'cbc.ca',
		match: /cbc.ca/i,
		removeParams: ['__vfz', 'cmp', 'referrer'],
	},
	{
		name: 'local12.com',
		match: /local12.com/i,
		removeParams: ['_gl'],
	},
	{
		name: 'eufylife.com',
		match: /eufylife.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'walmart.com',
		hosts: ['walmart.com'],
		match: /walmart.com/i,
		removeParams: [
			'athAsset',
			'povid',
			'wmlspartner',
			'athcpid',
			'athpgid',
			'athznid',
			'athmtid',
			'athstid',
			'athguid',
			'athwpid',
			'athtvid',
			'athcgid',
			'athieid',
			'athancid',
			'athbdg',
			'campaign_id',
			'eventST',
			'bt',
			'pos',
			'rdf',
			'tax',
			'plmt',
			'mloc',
			'pltfm',
			'pgId',
			'pt',
			'spQs',
			'adUid',
			'adsRedirect',
		],
		redirectParam: 'rd',
	},
	{
		name: 'adclick.g.doubleclick.net',
		hosts: ['adclick.g.doubleclick.net'],
		match: /adclick.g.doubleclick.net/i,
		redirectParam: 'adurl',
	},
	{
		name: 'dyno.gg',
		match: /dyno.gg/i,
		removeParams: ['ref'],
	},
	{
		name: 'eufylife.com',
		match: /eufylife.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'connect.studentbeans.com',
		match: /connect.studentbeans.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'urldefense.proofpoint.com',
		hosts: ['urldefense.proofpoint.com'],
		match: /urldefense.proofpoint.com/i,
		decode: {
			param: 'u',
			handler: 'urldefense.proofpoint.com',
		},
	},
	{
		name: 'curseforge.com',
		hosts: ['curseforge.com'],
		match: /www.curseforge.com/i,
		redirectParam: 'remoteurl',
	},
	{
		name: 's.pemsrv.com',
		hosts: ['s.pemsrv.com'],
		match: /s.pemsrv.com/i,
		removeParams: ['cat', 'idzone', 'type', 'sub', 'block', 'el', 'tags', 'cookieconsent', 'scr_info'],
		redirectParam: 'p',
	},
	{
		name: 'chess.com',
		match: /www.chess.com/i,
		removeParams: ['c'],
	},
	{
		name: 'porndude.link',
		match: /porndude.link/i,
		removeParams: ['ref'],
	},
	{
		name: 'xvideos.com',
		match: /xvideos.com/i,
		removeParams: ['sxcaf'],
	},
	{
		name: 'xvideos.red',
		match: /xvideos.red/i,
		removeParams: ['sxcaf', 'pmsc', 'pmln'],
	},
	{
		name: 'xhamster.com',
		match: /xhamster.com/i,
		removeParams: ['source'],
	},
	{
		name: 'patchbot.io',
		hosts: ['patchbot.io'],
		match: /patchbot.io/i,
		decode: {
			targetPath: true,
			handler: 'patchbot.io',
		},
	},
	{
		name: 'milkrun.com',
		match: /milkrun.com/i,
		removeParams: ['_branch_match_id', '_branch_referrer'],
	},
	{
		name: 'gog.salesmanago.com',
		hosts: ['gog.salesmanago.com'],
		match: /gog.salesmanago.com/i,
		removeParams: ['smclient', 'smconv', 'smlid'],
		redirectParam: 'url',
	},
	{
		name: 'dailymail.co.uk',
		match: /dailymail.co.uk/i,
		removeParams: ['reg_source', 'ito'],
	},
	{
		name: 'stardockentertainment.info',
		hosts: ['stardockentertainment.info'],
		match: /www.stardockentertainment.info/i,
		decode: {
			targetPath: true,
			handler: 'stardockentertainment.info',
		},
	},
	{
		name: 'steam.gs',
		hosts: ['steam.gs'],
		match: /steam.gs/i,
		decode: {
			targetPath: true,
			handler: 'steam.gs',
		},
	},
	{
		name: '0yxjo.mjt.lu',
		hosts: ['0yxjo.mjt.lu'],
		match: /0yxjo.mjt.lu/i,
		decode: {
			targetPath: true,
			handler: '0yxjo.mjt.lu',
		},
	},
	{
		name: 'click.redditmail.com',
		hosts: ['click.redditmail.com'],
		match: /click.redditmail.com/i,
		decode: {
			targetPath: true,
			handler: 'click.redditmail.com',
		},
	},
	{
		name: 'deals.dominos.co.nz',
		hosts: ['deals.dominos.co.nz'],
		match: /deals.dominos.co.nz/i,
		decode: {
			targetPath: true,
			handler: 'deals.dominos.co.nz',
		},
	},
	{
		name: 'hashnode.com',
		match: /(?:.*\.)?hashnode\.com/i,
		removeParams: ['source'],
	},
	{
		name: 's.amazon-adsystem.com',
		hosts: ['s.amazon-adsystem.com'],
		match: /s.amazon-adsystem.com/i,
		removeParams: ['dsig', 'd', 'ex-fch', 'ex-fargs', 'cb'],
		redirectParam: 'rd',
	},
	{
		name: 'hypable.com',
		hosts: ['hypable.com'],
		match: /www.hypable.com/i,
		AMP: {replace: {text: /amp\/$/gi}},
	},
	{
		name: 'theguardian.com',
		hosts: ['theguardian.com'],
		match: /theguardian.com/i,
		AMP: {
			replace: {
				text: 'amp.theguardian.com',
				with: 'theguardian.com',
			},
		},
		removeParams: ['INTCMP', 'acquisitionData', 'REFPVID'],
	},
	{
		name: 'indiatoday.in',
		hosts: ['indiatoday.in'],
		match: /www.indiatoday.in/i,
		AMP: {
			replace: {
				text: 'www.indiatoday.in/amp/',
				with: 'www.indiatoday.in/',
			},
		},
	},
	{
		name: 'seek.co.nz',
		match: /www.seek.co.nz/i,
		removeParams: ['tracking', 'sc_trk'],
	},
	{
		name: 'seekvolunteer.co.nz',
		match: /seekvolunteer.co.nz/i,
		removeParams: ['tracking', 'sc_trk'],
	},
	{
		name: 'seekbusiness.com.au',
		match: /www.seekbusiness.com.au/i,
		removeParams: ['tracking', 'cid'],
	},
	{
		name: 'garageclothing.com',
		match: /www.garageclothing.com/i,
		removeParams: ['syte_ref', 'site_ref'],
	},
	{
		name: 'urbandictionary.com',
		match: /urbandictionary.com/i,
		removeParams: ['amp'],
	},
	{
		name: 'norml.org',
		match: /norml.org/i,
		removeParams: ['amp'],
	},
	{
		name: 'nbcconnecticut.com',
		match: /www.nbcconnecticut.com/i,
		removeParams: ['amp'],
	},
	{
		name: 'pbs.org',
		hosts: ['pbs.org'],
		match: /www.pbs.org/i,
		AMP: {
			replace: {
				text: 'pbs.org/newshour/amp/',
				with: 'pbs.org/newshour/',
			},
		},
	},
	{
		name: 'm.thewire.in',
		hosts: ['m.thewire.in'],
		match: /m.thewire.in/i,
		AMP: {
			replace: {
				text: 'm.thewire.in/article/',
				with: 'thewire.in/',
			},
			sliceTrailing: '/amp',
		},
	},
	{
		name: 'ladysmithchronicle.com',
		match: /www.ladysmithchronicle.com/i,
		removeParams: ['ref'],
	},
	{
		name: 'businesstoday.in',
		hosts: ['businesstoday.in'],
		match: /www.businesstoday.in/i,
		AMP: {
			replace: {
				text: 'www.businesstoday.in/amp/markets/',
				with: 'www.businesstoday.in/markets/',
			},
		},
	},
	{
		name: 'turnto10.com',
		hosts: ['turnto10.com'],
		match: /turnto10.com/i,
		AMP: {
			replace: {
				text: 'turnto10.com/amp/',
				with: 'turnto10.com/',
			},
		},
	},
	{
		name: 'gadgets360.com',
		hosts: ['gadgets360.com'],
		match: /www.gadgets360.com/i,
		AMP: {
			sliceTrailing: '/amp',
		},
	},
	{
		name: 'm10news.com',
		match: /m10news.com/i,
		removeParams: ['amp'],
	},
	{
		name: 'elprogreso.es',
		hosts: ['elprogreso.es'],
		match: /www.elprogreso.es/i,
		AMP: {
			replace: {
				text: '.amp.html',
				with: '.html',
			},
		},
	},
	{
		name: 'thenewsminute.com',
		hosts: ['thenewsminute.com'],
		match: /www.thenewsminute.com/i,
		AMP: {
			replace: {
				text: 'www.thenewsminute.com/amp/story/',
				with: 'www.thenewsminute.com/',
			},
		},
	},
	{
		name: 'mirror.co.uk',
		hosts: ['mirror.co.uk'],
		match: /www.mirror.co.uk/i,
		AMP: {
			sliceTrailing: '.amp',
		},
	},
	{
		name: 'libretro.com',
		match: /www.libretro.com/i,
		removeParams: ['amp'],
	},
	{
		name: 'the-sun.com',
		hosts: ['the-sun.com'],
		match: /www.the-sun.com/i,
		AMP: {
			sliceTrailing: 'amp/',
		},
	},
	{
		name: 'bostonherald.com',
		hosts: ['bostonherald.com'],
		match: /bostonherald.com/i,
		removeParams: ['g2i_source', 'g2i_medium', 'g2i_campaign'],
		AMP: {
			sliceTrailing: 'amp/',
		},
	},
	{
		name: 'playshoptitans.com',
		match: /playshoptitans.com/i,
		removeParams: ['af_ad', 'pid', 'source_caller', 'shortlink', 'c'],
	},
	{
		name: 'news.com.au',
		match: /www.news.com.au/i,
		removeParams: ['sourceCode'],
	},
	{
		name: 'wvva.com',
		match: /www.wvva.com/i,
		removeParams: ['outputType'],
	},
	{
		name: 'realestate.com.au',
		match: /www.realestate.com.au/i,
		removeParams: [
			'campaignType',
			'campaignChannel',
			'campaignName',
			'campaignContent',
			'campaignSource',
			'campaignPlacement',
			'sourcePage',
			'sourceElement',
			'cid',
		],
	},
	{
		name: 'redirectingat.com',
		hosts: ['redirectingat.com'],
		match: /redirectingat.com/i,
		redirectParam: 'url',
		removeParams: ['id', 'xcust', 'xs'],
		decode: {handler: 'redirectingat.com', targetPath: true},
	},
	{
		name: 'map.sewoon.org',
		match: /map.sewoon.org/i,
		removeParams: ['cid'],
	},
	{
		name: 'vi-control.net',
		match: /[&?]source=https:\/\/vi-control\.net\/community$/,
		matchURL: true,
		removeParams: ['source'],
	},
	{
		name: 'rosequake.com',
		hosts: ['rosequake.com'],
		match: /www.rosequake.com/i,
		removeParams: ['edmID', 'linkID', 'userID', 'em', 'taskItemID'],
		redirectParam: 'to',
	},
	{
		name: 'rekrute.com',
		hosts: ['rekrute.com'],
		match: /www.rekrute.com/i,
		removeParams: ['clear'],
		redirectParam: 'keyword',
	},
	{
		name: 'go.skimresources.com',
		hosts: ['go.skimresources.com'],
		match: /go.skimresources.com/i,
		removeParams: ['id', 'xs', 'xcust'],
		redirectParam: 'url',
	},
	{
		name: 'khnum-ezi.com',
		match: /khnum-ezi.com/i,
		removeParams: [
			'browserWidth',
			'browserHeight',
			'iframeDetected',
			'webdriverDetected',
			'gpu',
			'timezone',
			'visitid',
			'type',
			'timezoneName',
		],
	},
	{
		name: 'theatlantic.com',
		match: /(?:www|accounts)\.theatlantic\.com/i,
		removeParams: ['source', 'referral'],
	},
	{
		name: 'rumble.com',
		match: /rumble.com/i,
		removeParams: ['e9s'],
	},
	{
		name: 'cointiply.com',
		match: /cointiply.com/i,
		removeParams: ['source_cta'],
	},
	{
		name: 'dev.to',
		hosts: ['dev.to'],
		match: /dev.to/,
		removeParams: ['t', 's', 'u'],
		redirectParam: 'u',
	},
	{
		name: 'milda-clq.com',
		match: /milda-clq.com/,
		removeParams: [
			'visitid',
			'type',
			'browserWidth',
			'browserHeight',
			'webdriverDetected',
			'timezone',
			'gpu',
			'iframeDetected',
			'timezoneName',
		],
	},
	{
		name: 'app.message.asce',
		match: /.*.app\.message\.asce\.org/i,
		allowParams: ['elq'],
	},
	{
		name: 'fernsehlotterie.de',
		match: /fernsehlotterie.de/i,
		removeEmptyParamValues: true,
	},
]);
